import { useState, useRef, useEffect } from 'react';
import { Send, Mic, Loader2, Square } from 'lucide-react';
import clsx from 'clsx';
import { useVoiceInput } from '../../hooks/useVoiceInput';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  suggestions?: string[];
}

export default function ChatInput({ onSend, disabled, suggestions }: ChatInputProps) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice: tap the mic, speak, tap again — the words go through onSend
  // exactly like typed text.
  const voice = useVoiceInput((spoken) => {
    if (!disabled) onSend(spoken);
  });

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim() && !disabled) {
      onSend(text.trim());
      setText('');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!disabled) {
      onSend(suggestion);
    }
  };

  return (
    <div className="border-t border-slate-200 bg-white">
      {/* Suggestions */}
      {suggestions && suggestions.length > 0 && (
        <div className="px-3 py-2 flex gap-2 flex-wrap border-b border-slate-100">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={disabled}
              className={clsx(
                'text-xs px-3 py-1.5 rounded-full transition-colors',
                disabled
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Voice notice — calm, self-clearing */}
      {voice.notice && (
        <p className="px-4 pt-2 text-xs text-slate-500">{voice.notice}</p>
      )}

      {/* Input form */}
      <form onSubmit={handleSubmit} className="p-3 flex gap-2">
        {voice.isSupported && (
          <button
            type="button"
            onClick={voice.toggle}
            disabled={disabled || voice.state === 'transcribing'}
            aria-label={voice.state === 'recording' ? 'Finish speaking' : 'Speak instead of typing'}
            className={clsx(
              'w-10 h-10 rounded-full flex items-center justify-center transition-colors shrink-0',
              voice.state === 'recording'
                ? 'bg-red-500 text-white animate-pulse'
                : voice.state === 'transcribing'
                  ? 'bg-slate-100 text-slate-400'
                  : disabled
                    ? 'bg-slate-100 text-slate-400'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )}
          >
            {voice.state === 'recording' ? (
              <Square className="w-4 h-4" fill="currentColor" />
            ) : voice.state === 'transcribing' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Mic className="w-4 h-4" />
            )}
          </button>
        )}
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            voice.state === 'recording'
              ? 'Listening — tap the square when you\'re done'
              : voice.state === 'transcribing'
                ? 'Making sense of it…'
                : disabled ? 'Thinking...' : 'Ask about the menu...'
          }
          disabled={disabled}
          className={clsx(
            'flex-1 px-4 py-2 text-sm rounded-full border transition-colors',
            disabled
              ? 'bg-slate-50 border-slate-200 text-slate-400'
              : 'bg-white border-slate-300 focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
          )}
        />
        <button
          type="submit"
          disabled={disabled || !text.trim()}
          className={clsx(
            'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
            disabled || !text.trim()
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          )}
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
